import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { automationApi } from '../api/client';

// Query keys
export const automationKeys = {
  all: ['automation'] as const,
  sla: () => [...automationKeys.all, 'sla'] as const,
  slaStatus: () => [...automationKeys.sla(), 'status'] as const,
  slaIssues: (assignedTo?: string) => [...automationKeys.sla(), 'issues', assignedTo] as const,
  score: () => [...automationKeys.all, 'score'] as const,
  scoreStats: () => [...automationKeys.score(), 'stats'] as const,
  config: () => [...automationKeys.all, 'config'] as const,
  configKey: (key: string) => [...automationKeys.config(), key] as const,
  blacklist: () => [...automationKeys.all, 'blacklist'] as const,
  locations: () => [...automationKeys.all, 'locations'] as const,
};

// SLA hooks
export function useSLAStatus() {
  return useQuery({
    queryKey: automationKeys.slaStatus(),
    queryFn: () => automationApi.getSLAStatus(),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useSLAIssues(assignedTo?: string) {
  return useQuery({
    queryKey: automationKeys.slaIssues(assignedTo),
    queryFn: () => automationApi.getSLAIssues(assignedTo),
    staleTime: 1000 * 60 * 2,
  });
}

export function useTriggerSLACheck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => automationApi.triggerSLACheck(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: automationKeys.sla() });
    },
  });
}

// Score hooks
export function useScoreStats() {
  return useQuery({
    queryKey: automationKeys.scoreStats(),
    queryFn: () => automationApi.getScoreStats(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useScoreLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadId: number) => automationApi.scoreLead(leadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: automationKeys.score() });
    },
  });
}

export function useScoreAllLeads() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (batchSize?: number) => automationApi.scoreAllLeads(batchSize),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: automationKeys.score() });
    },
  });
}

// Config hooks
export function useAutomationConfigs() {
  return useQuery({
    queryKey: automationKeys.config(),
    queryFn: () => automationApi.getConfigs(),
  });
}

export function useAutomationConfig<T>(key: string) {
  return useQuery({
    queryKey: automationKeys.configKey(key),
    queryFn: () => automationApi.getConfig<T>(key),
  });
}

export function useSetAutomationConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, value, description }: { key: string; value: unknown; description?: string }) =>
      automationApi.setConfig(key, value, description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: automationKeys.config() });
    },
  });
}

// Blacklist hooks
export function useBlacklist() {
  return useQuery({
    queryKey: automationKeys.blacklist(),
    queryFn: () => automationApi.getBlacklist(),
  });
}

export function useAddBlacklistEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: automationApi.addBlacklistEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: automationKeys.blacklist() });
    },
  });
}

export function useDeleteBlacklistEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => automationApi.deleteBlacklistEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: automationKeys.blacklist() });
    },
  });
}

// Target locations hooks
export function useTargetLocations() {
  return useQuery({
    queryKey: automationKeys.locations(),
    queryFn: () => automationApi.getTargetLocations(),
  });
}

export function useSetTargetLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: automationApi.setTargetLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: automationKeys.locations() });
    },
  });
}

export function useDeleteTargetLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => automationApi.deleteTargetLocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: automationKeys.locations() });
    },
  });
}
